import { useState, useCallback, useEffect } from "react";
import { Button } from "~/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "~/components/ui/input-otp";
import { Label } from "~/components/ui/label";
import { FaArrowRightLong } from "react-icons/fa6";
import { Input } from "~/components/ui/input";
import loginBirbyRightImg from "~/assets/login-birby-right.svg";
import loginGoogleImg from "~/assets/login-google.svg";
import loginFacebookImg from "~/assets/login-facebook.svg";
import loginAppleImg from "~/assets/login-apple.svg";
import { LegalDocDialog } from "~/components/common/LegalDocDialog";
import { cn } from "~/lib/utils";
import { useUserStore } from "~/lib/stores/user";
import { performGoogleLogin, facebookSDK } from "~/lib/social-login";
import { performAppleLogin, isAppleLoginAvailable } from "~/lib/apple-login";
import { apiErrorMessage } from "~/api/client";
import { devLog } from "~/lib/dev-log";

// The `dark` look, for the outline buttons and the input fields.
const DARK_BUTTON = "border-white/10 bg-neutral-800 text-neutral-50 hover:bg-neutral-700 hover:text-neutral-50";
const DARK_FIELD = "border-white/10 bg-neutral-800 text-neutral-50 placeholder:text-neutral-500";

// ============================================================================
// Error Handling Helper
// ============================================================================

function handleAuthError(
  error: any,
  logMessage: string,
  defaultErrorMessage: string,
  onError?: (error: string) => void
) {
  devLog(logMessage, error);
  onError?.(apiErrorMessage(error, defaultErrorMessage));
}

// ============================================================================
// Login Handlers - Centralized login logic
// ============================================================================

interface UseLoginHandlersProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onEmailSent?: (email: string) => void;
  invitationCode?: string;
}

function useLoginHandlers({ onSuccess, onError, onEmailSent, invitationCode }: UseLoginHandlersProps = {}) {
  const { finishDelayedSignup, emailLogin, firebaseLogin, appleLogin, facebookLogin } = useUserStore();

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      try {
        const response = await finishDelayedSignup(email, invitationCode);
        if (response === "email-sent") {
          onEmailSent?.(email);
        } else if (response === "authenticated") {
          onSuccess?.();
        }
      } catch (error: any) {
        handleAuthError(error, "Finish delayed signup failed:", "Failed to signup. Please try again.", onError);
      }
    },
    [finishDelayedSignup, onSuccess, onError, onEmailSent, invitationCode]
  );

  const handleOTPSubmit = useCallback(
    async (email: string, otpToken: string) => {
      try {
        const response = await emailLogin(email, otpToken);
        if (response === "authenticated") {
          onSuccess?.();
        }
      } catch (error: any) {
        handleAuthError(error, "Email login failed:", "Failed to verify code. Please try again.", onError);
      }
    },
    [emailLogin, onSuccess, onError]
  );

  const handleGoogleLogin = useCallback(async () => {
    try {
      const result = await performGoogleLogin();
      const response = await firebaseLogin(result.idToken, result.email, "google", invitationCode);
      if (response === "authenticated") {
        onSuccess?.();
      }
    } catch (error: any) {
      handleAuthError(error, "Google login failed:", "Failed to sign in with Google. Please try again.", onError);
    }
  }, [firebaseLogin, onSuccess, onError, invitationCode]);

  const handleAppleLogin = useCallback(async () => {
    try {
      const result = await performAppleLogin();
      const response = await appleLogin(result.code, result.email, result.firstName, result.lastName, invitationCode);
      if (response === "authenticated") {
        onSuccess?.();
      }
    } catch (error: any) {
      handleAuthError(error, "Apple login failed:", "Failed to sign in with Apple. Please try again.", onError);
    }
  }, [appleLogin, onSuccess, onError, invitationCode]);

  const handleFacebookLogin = useCallback(async () => {
    try {
      const result = await facebookSDK.login();
      const accessToken = result.accessToken;
      if (!accessToken) {
        throw new Error("Facebook account did not provide access token");
      }
      const userInfo = result.userInfo;
      if (userInfo.error) {
        throw new Error(`Facebook login failed: ${userInfo.error}`);
      }
      const response = await facebookLogin(accessToken, result.userInfo, undefined, invitationCode);
      if (response === "authenticated") {
        onSuccess?.();
      }
    } catch (error: any) {
      handleAuthError(error, "Facebook login failed:", "Failed to sign in with Facebook. Please try again.", onError);
    }
  }, [facebookLogin, onSuccess, onError, invitationCode]);

  return {
    handleEmailSubmit,
    handleOTPSubmit,
    handleGoogleLogin,
    handleAppleLogin,
    handleFacebookLogin,
  };
}

// ============================================================================
// LoginFlow - Complete login flow with email/OTP state management
// ============================================================================

export interface LoginFlowProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  showBackButton?: boolean;
  className?: string;
  title?: string | React.ReactNode;
  subtitle?: string;
  showTerms?: boolean;
  initialEmail?: string;
  invitationCode?: string;
  /** For a dark surface: the form draws no card of its own and takes light text. */
  dark?: boolean;
}

export function LoginFlow({
  onSuccess,
  onError,
  showBackButton = true,
  className,
  title = "Welcome to Blidz!",
  subtitle,
  showTerms = true,
  initialEmail,
  invitationCode,
  dark = false,
}: LoginFlowProps) {
  const [emailSent, setEmailSent] = useState<boolean>(!!initialEmail);
  const [email, setEmail] = useState<string | undefined>(initialEmail);

  // Update state when initialEmail changes
  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      setEmailSent(true);
    } else {
      setEmailSent(false);
      setEmail(undefined);
    }
  }, [initialEmail]);

  const handleEmailSent = useCallback((email: string) => {
    setEmail(email);
    setEmailSent(true);
  }, []);

  const handleBack = useCallback(() => {
    setEmailSent(false);
  }, []);

  return emailSent && email ? (
    <LoginOTPForm
      email={email}
      onBack={showBackButton ? handleBack : undefined}
      onSuccess={onSuccess}
      onError={onError}
      showBackButton={showBackButton}
      className={className}
      dark={dark}
    />
  ) : (
    <LoginForm
      onSuccess={onSuccess}
      onError={onError}
      onEmailSent={handleEmailSent}
      className={className}
      title={title}
      subtitle={subtitle}
      showTerms={showTerms}
      invitationCode={invitationCode}
      dark={dark}
    />
  );
}

// ============================================================================
// LoginOTPForm - Standalone OTP verification form with integrated auth
// ============================================================================

export interface LoginOTPFormProps {
  email: string;
  onBack?: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  showBackButton?: boolean;
  className?: string;
  dark?: boolean;
}

export function LoginOTPForm({
  email,
  onBack,
  onSuccess,
  onError,
  showBackButton = true,
  className,
  dark = false,
}: LoginOTPFormProps) {
  const [otpToken, setOtpToken] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [otpError, setOtpError] = useState<string>("");

  // Use integrated login handlers
  const { handleOTPSubmit } = useLoginHandlers({ onSuccess, onError });

  const validateOTP = (otp: string): boolean => {
    if (otp.length === 0) {
      setOtpError("Verification code is required");
      return false;
    }
    if (otp.length !== 6) {
      setOtpError("Please enter the complete 6-digit code");
      return false;
    }
    if (!/^\d{6}$/.test(otp)) {
      setOtpError("Verification code must contain only numbers");
      return false;
    }
    setOtpError("");
    return true;
  };

  const handleInput = (value: string) => {
    setOtpToken(value);
    if (otpError) {
      validateOTP(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateOTP(otpToken)) {
      return;
    }

    setIsLoading(true);
    try {
      await handleOTPSubmit(email, otpToken);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("flex flex-col items-center justify-center", className)}>
      <div
        className={cn(
          "flex w-full max-w-[573px] flex-col items-center justify-center gap-6 rounded-[16px] sm:gap-8",
          dark ? "" : "bg-white p-6 sm:p-8"
        )}
      >
        <img src={loginBirbyRightImg} alt="Birby Logo" className="h-24 w-24 sm:h-28 sm:w-28" />
        <div className="flex flex-col items-center justify-center">
          <div
            className={cn(
              "text-center text-[24px]/[28px] font-[700] lg:text-[30px]/[34px]",
              dark ? "text-white" : "text-[#171717]"
            )}
          >
            Verify your email address
          </div>
          <p
            className={cn(
              "mt-[8px] text-center text-[12px]/[18px] font-[500] lg:text-[14px]/[20px]",
              dark ? "text-neutral-400" : "text-[#525252]"
            )}
          >
            A six-digit code was emailed to <span className="font-semibold">{email}</span>. Input it below to verify
            your email login.
          </p>
          <div className="h-4 sm:h-5" />
          <form onSubmit={handleSubmit}>
            <div>
              <Label
                htmlFor="otp-input"
                className={cn(
                  "text-[12px]/[18px] font-[500] lg:text-[14px]/[20px]",
                  dark ? "text-neutral-300" : "text-[#0F172A]"
                )}
              >
                Verification code
              </Label>
              <div
                className={cn(
                  "mt-[6px]",
                  otpError ? "rounded-[8px] border border-red-500 hover:border-red-600" : "hover:border-gray-400"
                )}
              >
                <InputOTP maxLength={6} id="otp-input" onChange={handleInput} autoFocus disabled={isLoading}>
                  <InputOTPGroup className="flex w-full flex-row gap-2 sm:gap-3">
                    <InputOTPSlot
                      index={0}
                      className={cn("aspect-square h-auto flex-1 transition-colors", dark && DARK_FIELD)}
                    />
                    <InputOTPSlot
                      index={1}
                      className={cn("aspect-square h-auto flex-1 transition-colors", dark && DARK_FIELD)}
                    />
                    <InputOTPSlot
                      index={2}
                      className={cn("aspect-square h-auto flex-1 transition-colors", dark && DARK_FIELD)}
                    />
                    <InputOTPSlot
                      index={3}
                      className={cn("aspect-square h-auto flex-1 transition-colors", dark && DARK_FIELD)}
                    />
                    <InputOTPSlot
                      index={4}
                      className={cn("aspect-square h-auto flex-1 transition-colors", dark && DARK_FIELD)}
                    />
                    <InputOTPSlot
                      index={5}
                      className={cn("aspect-square h-auto flex-1 transition-colors", dark && DARK_FIELD)}
                    />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {otpError && <p className="mt-2 text-sm text-red-500">{otpError}</p>}
            </div>
            <div className="h-4 sm:h-5" />
            <Button
              variant="yellow"
              size="lg"
              type="submit"
              disabled={isLoading || !!otpError}
              className="h-[40px] w-full rounded-[16px] text-[12px]/[18px] font-[600] lg:text-[14px]/[20px]"
            >
              {isLoading ? "Verifying..." : "Continue"}
              {!isLoading && <FaArrowRightLong />}
            </Button>

            {showBackButton && onBack && (
              <>
                <div className="h-[12px]" />
                <Button
                  variant="outline"
                  size="lg"
                  type="button"
                  onClick={onBack}
                  disabled={isLoading}
                  className={cn(
                    "h-[40px] w-full rounded-[16px] text-[12px]/[18px] font-[600] lg:text-[14px]/[20px]",
                    dark && DARK_BUTTON
                  )}
                >
                  Back
                </Button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LoginForm - Standalone login form with integrated auth
// ============================================================================

export interface LoginFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onEmailSent?: (email: string) => void;
  className?: string;
  title?: string | React.ReactNode;
  subtitle?: string;
  showTerms?: boolean;
  invitationCode?: string;
  dark?: boolean;
}

export function LoginForm({
  onSuccess,
  onError,
  onEmailSent,
  className,
  title = "Welcome to Blidz!",
  subtitle,
  showTerms = true,
  invitationCode,
  dark = false,
}: LoginFormProps) {
  const [email, setEmail] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>("");

  // Use integrated login handlers
  const { handleEmailSubmit, handleGoogleLogin, handleAppleLogin, handleFacebookLogin } = useLoginHandlers({
    onSuccess,
    onError,
    onEmailSent,
    invitationCode,
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError("Email is required");
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      validateEmail(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);
    try {
      await handleEmailSubmit(email);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLoginClick = async () => {
    setIsLoading(true);
    try {
      await handleGoogleLogin();
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookLoginClick = async () => {
    setIsLoading(true);
    try {
      await handleFacebookLogin();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleLoginClick = async () => {
    setIsLoading(true);
    try {
      await handleAppleLogin();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("w-full space-y-4", className)}>
      {(title || subtitle) && (
        <div className="text-center">
          {title &&
            (typeof title === "string" ? (
              <h2 className={cn("text-2xl font-bold md:text-3xl", dark ? "text-white" : "text-gray-900")}>{title}</h2>
            ) : (
              <div>{title}</div>
            ))}
          {subtitle && <p className={cn("mt-2 text-sm", dark ? "text-neutral-400" : "text-gray-600")}>{subtitle}</p>}
        </div>
      )}

      <div className="space-y-3">
        {isAppleLoginAvailable() && (
          <Button
            type="button"
            variant="outline"
            size="lg"
            className={cn("h-12 w-full rounded-2xl", dark && DARK_BUTTON)}
            onClick={handleAppleLoginClick}
            disabled={isLoading}
          >
            {/* The Apple mark is drawn black. */}
            <img src={loginAppleImg} alt="Apple" className={cn("mr-3 h-5 w-5", dark && "invert")} />
            <span className="text-sm font-semibold">Continue with Apple</span>
          </Button>
        )}

        <Button
          type="button"
          variant="outline"
          size="lg"
          className={cn("h-12 w-full rounded-2xl", dark && DARK_BUTTON)}
          onClick={handleGoogleLoginClick}
          disabled={isLoading}
        >
          <img src={loginGoogleImg} alt="Google" className="mr-3 h-5 w-5" />
          <span className="text-sm font-semibold">Continue with Google</span>
        </Button>

        <Button
          type="button"
          variant="outline"
          size="lg"
          className={cn("h-12 w-full rounded-2xl", dark && DARK_BUTTON)}
          onClick={handleFacebookLoginClick}
          disabled={isLoading}
        >
          <img src={loginFacebookImg} alt="Facebook" className="mr-3 h-5 w-5" />
          <span className="text-sm font-semibold">Continue with Facebook</span>
        </Button>
      </div>

      <div className="flex items-center">
        <div className={cn("flex-1 border-t", dark ? "border-white/10" : "border-gray-200")} />
        <span className={cn("px-4 text-sm", dark ? "text-neutral-500" : "bg-white text-gray-500")}>OR</span>
        <div className={cn("flex-1 border-t", dark ? "border-white/10" : "border-gray-200")} />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={handleInput}
            disabled={isLoading}
            className={cn(
              "h-12 rounded-2xl transition-colors",
              dark && DARK_FIELD,
              emailError ? "border-red-500 focus:border-red-500" : ""
            )}
          />
          {emailError && <p className="mt-1 text-sm text-red-500">{emailError}</p>}
        </div>

        <Button
          variant="yellow"
          size="lg"
          type="submit"
          disabled={isLoading || !!emailError || !email.trim()}
          className="h-12 w-full rounded-2xl text-sm font-semibold"
        >
          {isLoading ? "Sending..." : "Continue"}
          {!isLoading && <FaArrowRightLong className="ml-2" />}
        </Button>
      </form>

      {showTerms && (
        <div className={cn("text-center text-xs", dark ? "text-neutral-500" : "text-gray-600")}>
          By continuing to sign in, you agree to our&nbsp;
          <LegalDocDialog doc="terms" title="Terms and Conditions">
            <button
              className={cn(
                "underline transition-colors hover:cursor-pointer",
                dark ? "hover:text-white" : "hover:text-black"
              )}
            >
              Terms
            </button>
          </LegalDocDialog>{" "}
          and acknowledge our{" "}
          <LegalDocDialog doc="privacy" title="Privacy Policy">
            <button
              className={cn(
                "underline transition-colors hover:cursor-pointer",
                dark ? "hover:text-white" : "hover:text-black"
              )}
            >
              Privacy Policy
            </button>
          </LegalDocDialog>
          .
        </div>
      )}
    </div>
  );
}
